import { InternalAxiosRequestConfig } from "axios";
import { maskPhone } from "@utils/func/maskPhone";
import { PRACTICE_FAKE_JWT } from "./practiceMode";

/**
 * 練習模式：onboarding 綁定/註冊的假後端（/app-api/register/* 家族 + line/login + sms-invite）。
 * --------------------------------------------------------------------------
 * 練習的掃碼情境帶 shopCode，會解析出真實 supplier_id（qrKnown），因此比對一律走這些端點
 * （不再退回 demoData 的本地比對）。為了讓「這不是我」能像正式流程一樣逐層降級
 * （手機 → 市話 → 地址 → 新客戶），這裡的比對結果**依輸入值決定**，只有下列練習值會命中：
 *   - 手機 0916375136        → 本瓦斯行比對到
 *   - 手機 0912345678        → 跨瓦斯行（在多家叫過）
 *   - 市話 04-2345678(0412345678) → 市話比對到
 *   - 地址含 西屯/南屯/大安   → 地址比對到
 * 其餘輸入一律「查無」，於是可一路降級到新客戶 walk-in。
 */

/** 練習用「會比對到」的輸入值（與 demoData 對齊；教練面板可據此指導客服）。 */
export const PRACTICE_MATCH_PHONE = "0916375136";
export const PRACTICE_CROSS_PHONE = "0912345678";
export const PRACTICE_MATCH_LANDLINE = "0412345678";
const KNOWN_ADDRESS_RE = /西屯|南屯|大安/;

const digits = (s: unknown) => String(s ?? "").replace(/\D/g, "");

const body = (config: InternalAxiosRequestConfig): Record<string, any> => {
  try {
    return typeof config.data === "string" ? JSON.parse(config.data) : ((config.data as Record<string, any>) ?? {});
  } catch {
    return {};
  }
};

// 練習用既有客戶（遮罩）——與 demoData 的 王＊明／西屯 對齊。
const MATCH_CUSTOMER = {
  cis_id: "PRACTICE-CIS-001",
  phone_masked: maskPhone(PRACTICE_MATCH_PHONE), // 09＊＊＊＊＊136
  address_masked: "台中市西屯區＊＊路 20 號",
};

const SUP_SELF = { supplier_id: "S001", supplier_name: "幸福瓦斯行" };

// 跨瓦斯行比對清單（demo：一支號碼在 永盛 / 幸福 都叫過，依最近叫桶排序）。
const CROSS_SUPPLIERS = [
  {
    supplier_id: "S002",
    supplier_name: "永盛瓦斯行",
    cis_id: "PRACTICE-CIS-002",
    phone_masked: MATCH_CUSTOMER.phone_masked,
    address_masked: "台中市南屯區＊＊街 8 號",
    supplier_phone: "04-2777-1234",
    supplier_address: "台中市南屯區文心路 100 號",
  },
  {
    supplier_id: "S001",
    supplier_name: "幸福瓦斯行",
    cis_id: MATCH_CUSTOMER.cis_id,
    phone_masked: MATCH_CUSTOMER.phone_masked,
    address_masked: MATCH_CUSTOMER.address_masked,
    supplier_phone: "04-2345-6789",
    supplier_address: "台中市西屯區文心路二段 100 號",
  },
];

// 推薦附近合作瓦斯行（無指定、逐層比對落空時，依座標推薦）。
const NEARBY = [
  { supplier_id: "S002", supplier_name: "永盛瓦斯行", city: "台中市", area: "南屯區", distance_km: 1.2, supplier_phone: "04-2777-1234", supplier_address: "台中市南屯區文心路 100 號" },
  { supplier_id: "S003", supplier_name: "大安瓦斯行", city: "台北市", area: "大安區", distance_km: 2.8, supplier_phone: "02-2701-5678", supplier_address: "台北市大安區信義路 50 號" },
];

/** 鋼瓶 barcode → 狀態（沿用 demoData 的樣本編號，讓練習與 demo 行為一致）。 */
const barcodeState = (code: string): "matched" | "joined" | "invalid" | "new" => {
  if (code.endsWith("8802")) return "joined";
  if (code.endsWith("8803")) return "invalid";
  if (code.endsWith("8804")) return "new";
  return "matched"; // 含練習模擬掃到的 TW-2024-8801
};

const has = (u: string, p: string) => u.includes(p);

/** 回傳 response body（含外層 { data }）；非 register/login 端點回 null 讓 practiceApi 續判。 */
export const resolveRegisterRequest = (config: InternalAxiosRequestConfig): { data: unknown } | null => {
  const u = config.url || "";
  const isReg = has(u, "/app-api/register/");
  const isLogin = has(u, "/app-api/line/login");
  const isSms = has(u, "/register-by-sms-invite");
  if (!isReg && !isLogin && !isSms) return null;

  const b = body(config);

  // 綁定/註冊成功後刷新 session → 回練習假 JWT，維持沙盒登入。
  if (isLogin) return { data: { jwtToken: PRACTICE_FAKE_JWT } };

  // 簡訊邀請靜默註冊 → 回假 JWT + 導向的練習訂單。
  if (isSms)
    return { data: { jwtToken: PRACTICE_FAKE_JWT, order_id: "po-001", cis_id: MATCH_CUSTOMER.cis_id, supplier_id: "S001" } };

  // shopCode → 瓦斯行公開資訊（練習掃碼情境靠這支解析出真實 supplier_id → 判定為「有指定」）
  // shopCode=NOSHOP → 回「查無」，用來重現「QR 帶了 shopCode 但後端解析不出瓦斯行」這個實際
  // 回報過的狀況（此時 entryKind 是否為 qrKnown 全靠網址 ?supplier= 的備援）。其餘一律解析成功。
  if (has(u, "/supplier-by-shop-code"))
    return String(b.shopCode ?? "") === "NOSHOP" ? { data: null } : { data: { supplier_id: "S001", supplier_name: "幸福瓦斯行", city: "台中市", phone: "04-2345-6789", cylinder_scan: true } };

  // 鋼瓶 barcode → 依樣本編號決定狀態
  if (has(u, "/cylinder-by-barcode")) {
    const state = barcodeState(String(b.barcode ?? ""));
    return {
      data: state === "matched"
        ? { state, supplier: SUP_SELF, customer: { ...MATCH_CUSTOMER, has_recent_order: true } }
        : { state, supplier: SUP_SELF },
    };
  }

  // --- 跨瓦斯行清單（須先於單一比對判斷：suppliers-by-xxx 內含 by-xxx）---
  if (has(u, "/match/suppliers-by-landline-address")) {
    const hit = digits(b.landline) === PRACTICE_MATCH_LANDLINE && KNOWN_ADDRESS_RE.test(String(b.address ?? ""));
    return { data: { suppliers: hit ? CROSS_SUPPLIERS : [] } };
  }
  if (has(u, "/match/suppliers-by-landline"))
    return { data: { suppliers: digits(b.landline) === PRACTICE_MATCH_LANDLINE ? CROSS_SUPPLIERS : [] } };
  if (has(u, "/match/suppliers-by-phone"))
    return { data: { suppliers: digits(b.phone) === PRACTICE_CROSS_PHONE ? CROSS_SUPPLIERS : [] } };
  if (has(u, "/match/suppliers-by-address")) {
    const hit = KNOWN_ADDRESS_RE.test(String(b.address ?? ""));
    // MatchedSupplierByAddressApi 無 address_masked
    return { data: { suppliers: hit ? CROSS_SUPPLIERS.map(({ address_masked, ...s }) => s) : [] } };
  }

  // --- 單一瓦斯行精確比對（須先於 by-landline 判斷 by-landline-address）---
  if (has(u, "/match/by-landline-address")) {
    const hit = digits(b.landline) === PRACTICE_MATCH_LANDLINE && KNOWN_ADDRESS_RE.test(String(b.address ?? ""));
    return { data: hit ? { matched: true, customer: { cis_id: MATCH_CUSTOMER.cis_id, phone_masked: MATCH_CUSTOMER.phone_masked } } : { matched: false } };
  }
  if (has(u, "/match/by-landline")) {
    const hit = digits(b.landline) === PRACTICE_MATCH_LANDLINE;
    return { data: hit ? { matched: true, customer: MATCH_CUSTOMER } : { matched: false } };
  }
  if (has(u, "/match/by-phone")) {
    const hit = digits(b.phone) === PRACTICE_MATCH_PHONE;
    return { data: hit ? { matched: true, customer: { cis_id: MATCH_CUSTOMER.cis_id, address_masked: MATCH_CUSTOMER.address_masked } } : { matched: false } };
  }
  if (has(u, "/match/by-address")) {
    const hit = KNOWN_ADDRESS_RE.test(String(b.address ?? ""));
    return { data: hit ? { matched: true, customer: { cis_id: MATCH_CUSTOMER.cis_id, phone_masked: MATCH_CUSTOMER.phone_masked } } : { matched: false } };
  }

  // --- 推薦附近合作瓦斯行（逐層落空時）---
  if (has(u, "/match/recommend-suppliers")) return { data: { suppliers: NEARBY } };

  // 新客戶 walk-in 建立／綁定既有 CIS
  if (has(u, "/walk-in"))
    return { data: { registration_id: "PRACTICE-REG-001", cis_id: MATCH_CUSTOMER.cis_id, customer_id: "900001", review_status: "approved", supplier_id: "S001" } };

  // 未綁定客戶登記（傳單有指定・自述既有但沒配到 → 待人工核對）
  if (has(u, "/unbound-customer")) return { data: { customer_id: 900001, outcome: "created" } };

  // 檔案上傳（簽名／鐵牌照）→ 回假 S3 key
  if (has(u, "/upload")) return { data: { s3_key: "practice/upload-key" } };

  // 其餘 register/* → 良性空資料
  return { data: {} };
};
